import { useThemeContext } from '../contexts/ThemeContext';

/**
 * Custom hook for theme management
 *
 * Provides access to theme state and controls with additional utilities
 * for working with the BiteWise theme system.
 *
 * @returns Theme context with additional utilities
 */
export const useTheme = () => {
  const context = useThemeContext();

  // Note: useThemeContext already throws an error if context is undefined,
  // so this check is redundant but kept for explicit error messaging
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  const { theme, actualTheme, setTheme, toggleTheme } = context;

  // Additional utility flags
  const isDark = actualTheme === 'dark';
  const isLight = actualTheme === 'light';
  const isSystem = theme === 'system';

  /**
   * Get theme-specific CSS classes
   */
  const getThemeClasses = (lightClass: string, darkClass: string): string => {
    return isDark ? darkClass : lightClass;
  };

  /**
   * Get theme-specific values
   */
  function getThemeValue<T>(lightValue: T, darkValue: T): T {
    return isDark ? darkValue : lightValue;
  }

  /**
   * Get elevation shadow classes based on theme
   */
  const getElevationClass = (level: 'soft' | 'medium' | 'strong'): string => {
    return isDark ? `shadow-blur-${level}` : `shadow-${level}`;
  };

  /**
   * Get theme-appropriate text color classes
   */
  const getTextColorClass = (variant: 'primary' | 'secondary' | 'muted' = 'primary'): string => {
    if (isDark) {
      switch (variant) {
        case 'primary':
          return 'text-base-content';
        case 'secondary':
          return 'text-base-content-secondary';
        case 'muted':
          return 'text-base-content-muted';
        default:
          return 'text-base-content';
      }
    } else {
      switch (variant) {
        case 'primary':
          return 'text-secondary-900';
        case 'secondary':
          return 'text-secondary-700';
        case 'muted':
          return 'text-secondary-500';
        default:
          return 'text-secondary-900';
      }
    }
  };

  /**
   * Get theme-appropriate background color classes
   */
  const getBackgroundClass = (variant: 'base' | 'elevated' | 'overlay' = 'base'): string => {
    if (isDark) {
      switch (variant) {
        case 'base':
          return 'bg-base-100';
        case 'elevated':
          return 'bg-surface-elevated';
        case 'overlay':
          return 'bg-surface-overlay';
        default:
          return 'bg-base-100';
      }
    } else {
      switch (variant) {
        case 'base':
          return 'bg-white';
        case 'elevated':
          return 'bg-white';
        case 'overlay':
          return 'bg-white';
        default:
          return 'bg-white';
      }
    }
  };

  /**
   * Get theme-appropriate border color classes
   */
  const getBorderClass = (variant: 'subtle' | 'default' = 'default'): string => {
    if (isDark) {
      return variant === 'subtle' ? 'border-border-subtle' : 'border-border-default';
    } else {
      return variant === 'subtle' ? 'border-secondary-200' : 'border-secondary-300';
    }
  };

  return {
    // Core theme state
    theme,
    actualTheme,
    setTheme,
    toggleTheme,

    // Utility flags
    isDark,
    isLight,
    isSystem,

    // Utility functions
    getThemeClasses,
    getThemeValue,
    getElevationClass,
    getTextColorClass,
    getBackgroundClass,
    getBorderClass,
  };
};

export default useTheme;